import { AfterViewInit, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingPanelService } from 'src/app/shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from 'src/app/shared/toast/toast.service';
import { CurrentUserService } from 'src/app/user/services';
import { SchedulerTask, SchedulerTaskPresentation } from '../../models/scheduler-task-objects';
import { SchedulerTaskService } from '../../services/scheduler-task.service';
import { DeveloperArtifactService } from 'src/app/developer/services/developer-artifact.service';
import { ArtifactAssemblyTypes, AssemblyArtifact } from 'src/app/developer/models/artifact';
import { SvgIconComponent } from '../../../shared/icons/svg-icon.component';
import { PermissionRequiredDirective } from '../../../shared/authentication/permission-required.directive';
import { ChangeLogLinkComponent } from '../../../shared/change-log/change-log-link/change-log-link.component';
import { ChangeLogLinkEntityDirective } from '../../../shared/change-log/change-log-link-entity/change-log-link-entity.directive';
import { DxDataGridModule } from 'devextreme-angular';
import { BizDataGridDirective } from '../../../shared/grid/biz-grid.directive';
import { DxoEditingModule, DxoTextsModule, DxoColumnFixingModule, DxiColumnModule, DxiButtonModule } from 'devextreme-angular/ui/nested';
import { BizUserColumnDirective } from '../../../shared/grid/biz-grid-user.directive';
import { BizDateColumnDirective } from '../../../shared/grid/biz-grid-date.directive';
import { DxTemplateModule } from 'devextreme-angular/core';
import { BizGridFiltervalues } from 'src/app/shared/grid/biz-grid-filtervalues';

@Component({
    selector: 'app-scheduler-list',
    templateUrl: './scheduler-task-list.component.html',
    styleUrls: ['./scheduler-task-list.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [SvgIconComponent, PermissionRequiredDirective, ChangeLogLinkComponent, ChangeLogLinkEntityDirective, DxDataGridModule, BizDataGridDirective, DxoEditingModule, DxoTextsModule, DxoColumnFixingModule, DxiColumnModule, BizUserColumnDirective, BizDateColumnDirective, DxiButtonModule, DxTemplateModule]
})
export class SchedulerTaskListComponent implements OnInit, AfterViewInit {
    schedulerTaskPresentations: SchedulerTaskPresentation[] = [];
    selectedSchedulerTasks: number[] = [];
    allowAdding = false;
    allowShowLog = false;
    allowDetails = false;

    hasEditPerm = false;
    hasDeletePerm = false;

    schedulerArtifacts: AssemblyArtifact[] = [];

    constructor(
        private router: Router,
        private loadingPanelService: LoadingPanelService,
        private toastService: ToastService,
        private schedulerTaskService: SchedulerTaskService,
        private currentUserService: CurrentUserService,
        public bizGridFiltervalues: BizGridFiltervalues,
        private developerArtifactService: DeveloperArtifactService
    ) {
        this.onAddItem = this.onAddItem.bind(this);
        this.allowUpdatingRow = this.allowUpdatingRow.bind(this);
        this.allowDeletingRow = this.allowDeletingRow.bind(this);
    }

    ngOnInit(): void {
        this.allowAdding = this.currentUserService.hasPermission('Scheduler_Action_Scheduling_Create');
        this.hasEditPerm = this.currentUserService.hasPermission('Scheduler_Action_Scheduling_Edit');
        this.hasDeletePerm = this.currentUserService.hasPermission('Scheduler_Action_Scheduling_Delete');
        this.allowShowLog = this.currentUserService.hasPermission('Developer_Action_ShowLog');
    }

    ngAfterViewInit(): void {
        void this.refresh();
    }

    get schedulerTasks(): SchedulerTask[] {
        return this.schedulerTaskPresentations.map(presentation => presentation.SchedulerTask);
    }

    onLogClicked(_: any): void {
        void this.navigateToLog(this.selectedSchedulerTasks[0]);
    }

    async refresh(): Promise<void> {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');

            this.schedulerTaskPresentations = [];
            this.schedulerArtifacts = [];
            [
                this.schedulerArtifacts,
                this.schedulerTaskPresentations
            ] = await Promise.all([
                await this.developerArtifactService.getAssemblyArtifacts(true, [ArtifactAssemblyTypes.TypeSchedulerTaskHandler]),
                await this.schedulerTaskService.getSchedulerTaskPresentations()
            ]);
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to load scheduler tasks...', ToastType.error);
        }
        finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }


    onToolbarPreparing(e: any): void {
        e.toolbarOptions.items.forEach((item: any) => {
            if (item.name === 'addRowButton') {
                item.options.onClick = this.onAddItem;
            }
        });
    }

    onAddItem(e: any): void {
        e.cancel = true;
        this.navigateToDetails(0);
    }

    onSelectionChanged(e: any): void {
        this.selectedSchedulerTasks = e.selectedRowsData.map((item: SchedulerTaskPresentation) => item.SchedulerTask.Id);
    }

    onEditingStart(e: any): void {
        e.cancel = true;
        this.navigateToDetails(e.data.SchedulerTask.Id);
    }

    onRowDblClick(e: any): void {
        if (this.allowDetails) {
            this.navigateToDetails(e.data.SchedulerTask.Id);
        }
    }

    async onRowRemoving(e: any): Promise<void> {
        const deferred = $.Deferred();
        e.cancel = deferred.promise();

        try {
            await this.schedulerTaskService.deleteSchedulerTask(e.data.SchedulerTask.Id);
            this.toastService.showToast('Scheduler task deleted...', ToastType.success);
            deferred.resolve(false);
        }
        catch (err) {
            console.error(err);
            this.toastService.showToast('Failed to delete scheduler task...', ToastType.error);
            deferred.resolve(true);
        }
    }

    navigateToDetails(schedulerTaskId: number): Promise<boolean> {
        return this.router.navigate(['settings/scheduler/details', { schedulerTaskId: schedulerTaskId }]);
    }
    navigateToLog(schedulerTaskId: number): Promise<boolean> {
        return this.router.navigate(['settings/scheduler/log', { schedulerTaskId: schedulerTaskId }]);
    }

    allowUpdatingRow(options: { component: any; row: any }): boolean {
        const item: SchedulerTaskPresentation = options?.row?.data;
        if (item) {
            return this.hasEditPerm && !item.SchedulerTask.IsSystem;
        }
        return false;
    }

      allowDeletingRow(options: { component: any; row: any }): boolean {
        const item: SchedulerTaskPresentation = options?.row?.data;
        if (item) {
            return this.hasDeletePerm && !item.SchedulerTask.IsSystem;
        }
        return false;
      }
}
